import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { MoreHorizontal, Eye, Trash2, Check, X, Settings } from "lucide-react";
import { useTran } from "@/hooks";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { PATHS } from "@/lib/router";
import { useUserListDerived, useUserStore } from "../hooks";
import { normalizeId } from "../utils/roles-permissions";

const UserActions = observer(({ user, onViewUser, onDeleteUser }) => {
  const { t } = useTran();
  const navigate = useNavigate();
  const { isAdminRole, currentUserId } = useUserListDerived();
  const { followUpUserId, assignFollowUp, revokeFollowUp, loadUsers } =
    useUserStore();
  const normalizedCurrentUserId = normalizeId(currentUserId);
  const rowId = normalizeId(user?.id || user?._id);
  const creatorId = normalizeId(user?.createdBy);
  const isMyUserRow =
    Boolean(normalizedCurrentUserId) &&
    ((rowId && rowId === normalizedCurrentUserId) ||
      (creatorId && creatorId === normalizedCurrentUserId));
  const disabled = !isAdminRole && !isMyUserRow;
  const followUpLoading = followUpUserId === user?.id;
  const showFollowUp = isAdminRole;

  if (disabled) {
    return (
      <Button variant="ghost" size="icon" aria-label={t("Actions")} disabled>
        <MoreHorizontal className="h-4 w-4" />
      </Button>
    );
  }

  const hasIncharge = Boolean(user?.incharge?.name);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={t("Actions")}>
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuLabel>{t("Actions")}</DropdownMenuLabel>
        <DropdownMenuItem onSelect={() => onViewUser?.(user)} className="gap-2">
          <Eye className="h-4 w-4 text-emerald-600" />
          {t("View")}
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => {
            const targetId = user?.id || user?.userId;
            if (!targetId) return;
            navigate(`${PATHS.MANAGE_USER}/${targetId}`);
          }}
          className="gap-2"
        >
          <Settings className="h-4 w-4 text-blue-600" />
          {t("Manage")}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {showFollowUp && (
          <>
            <DropdownMenuItem
              onSelect={async () => {
                if (followUpLoading) return;
                if (hasIncharge) {
                  const result = await revokeFollowUp(user?.id);
                  if (result?.success) {
                    loadUsers();
                  }
                } else {
                  const result = await assignFollowUp(user?.id);
                  if (result?.success) {
                    loadUsers();
                  }
                }
              }}
              disabled={followUpLoading}
              className="gap-2"
            >
              {hasIncharge ? (
                <X className="h-4 w-4 text-red-600" />
              ) : (
                <Check className="h-4 w-4 text-blue-600" />
              )}
              {hasIncharge ? t("Unfollow Up") : t("Follow Up")}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem
          onSelect={() => onDeleteUser?.(user)}
          className="gap-2"
          variant="destructive"
        >
          <Trash2 className="h-4 w-4" />
          {t("Delete")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

export default UserActions;
